import { MIN_PASSWORD_LENGTH } from '@iac/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Field } from '../components/Field';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { useDict } from '../i18n/dictionary';
import { shell } from '../i18n/shell';
import { api, unwrap } from '../lib/api';
import { useAuth } from '../lib/auth';

export function Profile() {
  const { session } = useAuth();
  const t = useDict(shell);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const change = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.auth.changePassword({
          body: { currentPassword, newPassword, newPasswordConfirm },
        }),
        200,
        t.passwordChangeFailed,
      ),
    meta: { success: t.passwordUpdated },
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    change.mutate(undefined, {
      onSuccess: () => {
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordConfirm('');
      },
    });
  }

  return (
    <section className="page-narrow">
      <PageHeader title={t.profile} />

      <Panel title={t.account}>
        <p className="form-hint">
          {t.signedInAs} <strong>{session?.user.login}</strong>.
        </p>
      </Panel>

      <Panel title={t.changePassword}>
        <form onSubmit={onSubmit} className="form-stack">
          <Field
            id="current-password"
            label={t.currentPassword}
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <Field
            id="new-password"
            label={t.newPassword}
            name="newPassword"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <Field
            id="new-password-confirm"
            label={t.confirmNewPassword}
            name="newPasswordConfirm"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
            value={newPasswordConfirm}
            onChange={(e) => setNewPasswordConfirm(e.target.value)}
          />
          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={change.isPending}
            >
              {t.updatePassword}
            </button>
          </div>
        </form>
      </Panel>
    </section>
  );
}
